import { cx } from "../../../utils/cx.ts";
import { themed } from "../../../utils/headless.ts";
import type { ClassesProps } from "../Types/general.ts";
import rawClasses from "./styles.module.css";

const classes = themed(rawClasses);

type InputProps = ClassesProps & {
	type:
		| "text"
		| "password"
		| "email"
		| "number"
		| "tel"
		| "url"
		| "search"
		| "date"
		| "time"
		| "datetime-local"
		| "month"
		| "week";
	label: string;
	id: string;
	valueMissing?: string;
	typeMismatch?: string;
	required?: boolean;
	disabled?: boolean;
};

/**
 * Renders a styled text input field with an associated label.
 *
 * @remarks
 * This component supports additional CSS classes, disabled and required states, and custom input types.
 *
 * @param {InputProps} props - The properties for the text input component.
 * @param props.id - The unique identifier for the input element.
 * @param props.label - The label text displayed above the input.
 * @param props.type - The type of the input (e.g., "text", "email", "password").
 * @param props.disabled - Whether the input is disabled.
 * @param props.required - Whether the input is required.
 * @param props.classes - Additional CSS classes to apply to the input container.
 *
 * @returns {JSX.Element} A JSX element representing a labeled text input field.
 */
export const Input = (props: InputProps) => {
	const { disabled = false, required = false } = props;
	const errorMessage = props.valueMissing || props.typeMismatch;
	const errorId = errorMessage ? `${props.id}-error` : undefined;

	return (
		<div className={cx(classes["form-field"], props.classes)}>
			<div className={classes["label-container"]}>
				<label className={classes.label} htmlFor={props.id}>
					{props.label}
				</label>
			</div>
			<input
				id={props.id}
				name={props.id}
				className={classes["text-input"]}
				type={props.type}
				required={required}
				disabled={disabled}
				aria-required={required || undefined}
				aria-invalid={errorMessage ? true : undefined}
				aria-describedby={errorId}
			/>
			{errorId && (
				<span id={errorId} role="alert">
					{errorMessage}
				</span>
			)}
		</div>
	);
};
